// CoweringDropDown.tsx


import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  IconArchive,
  IconArchiveOff,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconEdit,
  IconTrash,
} from "@tabler/icons-react";
import { useDropdown } from "./hooks/useDropdown";
import { addOutsidePressListener } from "@/lib/slotting/outsidePress";
import { Plugin } from "../../types/enums/Plugin";
import { RegistrationType } from "../../types/enums/RegistrationType";
import { EventRowDTO } from "../../types/EventRowDTO";
import { resolveEventCaps } from "@/lib/authz/eventLanding";
import { Capability } from "@/types/Capability";

type CoweringDropDownProps = {
  loading?: boolean;
  disabled?: boolean;
  changeDefault?: boolean;
  border?: boolean;
  dialogTop?: boolean;
  translate?: boolean;
  translation?: (key: string, values?: Record<string, string>) => string;
  initialItem: string;
  allItems: string[];
  onDropdownAction: (item: string) => void;
  onHoverAction?: (item: string) => void;
  onNestedAction?: (parentItem: string, subItem: string) => void;
  event?: EventRowDTO;
};

type NestedItems = {
  [key: string]: string[];
};

export const CoweringDropDown: React.FC<CoweringDropDownProps> = ({
  loading = false,
  disabled = false,
  changeDefault = true,
  border = false,
  dialogTop = false,
  translation: t,
  translate = false,
  initialItem,
  allItems,
  onDropdownAction,
  onHoverAction,
  onNestedAction,
  event,
}) => {
  const {
    isDropdownOpen,
    selectedItem,
    items,
    setIsDropdownOpen,
    handleSelect,
  } = useDropdown({
    initialItem,
    allItems,
    onDropdownAction,
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  // Portaling the menu to <body> breaks the trigger→menu hover bridge that
  // DOM-nesting used to provide (the menu is no longer a descendant of the
  // trigger, so leaving the trigger fires its mouseleave). A short close delay
  // lets the pointer cross the gap into the menu, whose mouseenter cancels it.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
      setActiveNestedMenu(null);
    }, 120);
  }, [cancelClose, setIsDropdownOpen]);
  useEffect(() => () => cancelClose(), [cancelClose]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");
  const [activeNestedMenu, setActiveNestedMenu] = useState<{
    item: string;
    side: "left" | "right";
    vAlign: "top" | "bottom";
  } | null>(null);

  // Dynamically generate nested menu items based on event plugins and registration type
  const getNestedItems = (): NestedItems => {
    const nested: NestedItems = {};

    if (!event?.plugins) return nested;

    const plugins = event.plugins;
    const registrationType = event.registrationType;

    // Gate each sub-page by the event-role's capabilities so a restricted
    // CUSTOM role never sees a sub it can't open (mirrors the sidebar gates).
    const caps = resolveEventCaps(
      event.role ?? "",
      event.capabilities as Capability[] | undefined,
    );
    const has = (...c: Capability[]) => c.some((x) => caps.has(x));

    // Guests/guestlist
    const guestItems: string[] = [];
    if (has("guests:read")) guestItems.push("guestlist");
    // requests needs the companions read AND a registration type that has them.
    if (
      has("guests:read.companions") &&
      (registrationType === RegistrationType.LOCKED ||
        registrationType === RegistrationType.INVITATION)
    ) {
      guestItems.push("requests");
    }
    if (guestItems.length > 0) nested["guests/guestlist"] = guestItems;

    // Check-In - nested menu with history and devices
    if (plugins.includes(Plugin.CHECK_IN)) {
      const checkInItems: string[] = [];
      if (has("check-in-logs:read")) checkInItems.push("history");
      if (has("check-in-devices:read")) checkInItems.push("devices");
      if (checkInItems.length > 0) nested["check-in"] = checkInItems;
    }

    // Messaging - only if MESSAGING plugin is enabled
    if (plugins.includes(Plugin.MESSAGING)) {
      const messagingItems: string[] = [];
      if (has("email-templates:read")) messagingItems.push("mail-templates");
      if (has("sent-emails:read")) messagingItems.push("sent-mails");
      if (has("event:settings:write")) messagingItems.push("sender");
      if (messagingItems.length > 0) {
        nested["messaging/email-templates"] = messagingItems;
      }
    }

    // Automation - nested menu with workflows and history
    if (plugins.includes(Plugin.ACTIONS) && has("workflows:read")) {
      nested["automation"] = ["workflows", "history"];
    }

    // Settings - always available with submenu
    nested["settings"] = ["general", "rules", "plugins", "collaboration"];

    // You can add more nested items based on other plugins
    if (plugins.includes(Plugin.DASHBOARD)) {
      // Add dashboard-specific nested items if needed
    }

    if (plugins.includes(Plugin.WEBSITE)) {
      // Add website-specific nested items if needed
    }

    return nested;
  };

  const nestedItems = getNestedItems();

  const getLabel = (key: string) => {
    if (!translate || !t) return key;
    try {
      return t(key);
    } catch {
      return key;
    }
  };

  // Filter items based on event role. settings/archive/duplicate are
  // OWNER/ADMIN-only event-admin operations: the settings sub-pages hard-gate on
  // role and duplicate/archive mutate the event. Previously only EDITOR/VIEWER
  // were stripped, leaking these to CUSTOM "individual" roles — now everyone but
  // OWNER/ADMIN is gated. The `event?.role` guard scopes this to the event-row
  // Actions menu; generic dropdowns (website PageActions) pass no event and keep
  // all their items (e.g. page "duplicate").
  const isOwnerOrAdmin = event?.role === "OWNER" || event?.role === "ADMIN";
  const filteredItems = items.filter((item) => {
    if (!event?.role) return true;
    if (
      (item === "settings" || item === "archive" || item === "duplicate") &&
      !isOwnerOrAdmin
    ) {
      return false;
    }
    return true;
  });

  const MARGIN = 8;

  const recompute = useCallback(() => {
    if (!triggerRef.current || !dropdownRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const spaceBelow = vh - triggerRect.bottom - MARGIN;
    const spaceAbove = triggerRect.top - MARGIN;

    const placeAbove =
      dialogTop ||
      (dropdownRect.height > spaceBelow && spaceAbove > spaceBelow);

    const top = placeAbove
      ? Math.max(MARGIN, triggerRect.top - dropdownRect.height)
      : triggerRect.bottom;

    const rawLeft =
      triggerRect.left + triggerRect.width / 2 - dropdownRect.width / 2;
    const left = Math.min(
      Math.max(MARGIN, rawLeft),
      Math.max(MARGIN, vw - dropdownRect.width - MARGIN),
    );

    setDropdownPosition({ top, left });
    setPlacement(placeAbove ? "top" : "bottom");
  }, [dialogTop]);

  useLayoutEffect(() => {
    if (isDropdownOpen) recompute();
  }, [isDropdownOpen, recompute]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const onResize = () => recompute();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isDropdownOpen, recompute]);

  const handleNestedMenuPosition = (
    parentItem: string,
    anchorEl: HTMLElement,
  ) => {
    const SUBMENU_W = 200;
    const itemCount = nestedItems[parentItem]?.length ?? 0;
    const SUBMENU_H = itemCount * 32 + 16;
    const liRect = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const side: "left" | "right" =
      liRect.right + SUBMENU_W + MARGIN > vw ? "left" : "right";
    const vAlign: "top" | "bottom" =
      liRect.top + SUBMENU_H + MARGIN > vh ? "bottom" : "top";

    setActiveNestedMenu({ item: parentItem, side, vAlign });
  };

  // Touch has neither mouseleave nor blur (iOS buttons don't focus on tap) —
  // close on any press outside trigger + portaled menu.
  useEffect(() => {
    if (!isDropdownOpen) return;
    return addOutsidePressListener((e) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsDropdownOpen(false);
    });
  }, [isDropdownOpen, setIsDropdownOpen]);

  if (loading) {
    return (
      <div className="w-24 h-5 surface-tertiary animate-pulse rounded-md"></div>
    );
  }

  return (
    <div
      ref={triggerRef}
      // pointerType-gated (per event, hybrids included): a finger tap fires
      // pointerenter with type "touch" and must not pre-open — the tap's
      // toggle click would instantly close it again. Touch closes via the
      // outside-press listener below (no mouseleave/blur on iOS).
      onPointerLeave={(e) => {
        if (!disabled && e.pointerType !== "touch") scheduleClose();
      }}
      onPointerEnter={(e) => {
        if (disabled || e.pointerType === "touch") return;
        cancelClose();
        setIsDropdownOpen(true);
      }}
      onClick={() => !disabled && setIsDropdownOpen((prev) => !prev)}
      className={`pl-3 w-max pr-2 py-1 group-hover:surface-secondary ${
        border ? "border rounded-lg font-tertiary" : ""
      } ${disabled ? "opacity-50" : ""} outline-quaternary`}
    >
      <div
        className={`inline-flex flex-row justify-center items-center cursor-pointer`}
      >
        <p className="body-2-medium">{selectedItem}</p>
        <IconChevronDown
          className={`ml-1 transition-transform duration-300 ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
          size={20}
        />
      </div>

      {/* Dropdown Menu — portaled to <body> so it escapes the table's
          `relative z-0` stacking context (Table.tsx). Rendered inline, the
          menu's `fixed z-50` was trapped at page-level z-0 and the sticky
          control bar (EventsCP, z-30, opaque) painted over its middle rows. */}
      {isDropdownOpen && !disabled && createPortal(
        <div
          onPointerEnter={(e) => {
            if (e.pointerType === "touch") return;
            cancelClose();
            setIsDropdownOpen(true);
          }}
          onPointerLeave={(e) => {
            if (e.pointerType !== "touch") scheduleClose();
          }}
          // The portal is a REACT child of the trigger div, whose onClick is
          // a toggle — synthetic clicks bubble through portals, so without
          // this stop any menu click would also flip the trigger toggle.
          // Item handlers close the menu themselves.
          onClick={(e) => e.stopPropagation()}
          ref={dropdownRef}
          // Portaling to <body> drops the text color AND size the menu used to
          // inherit from the trigger (font-tertiary when `border`, body-2 from
          // the table). Re-apply both so the menu matches the table's type.
          className={`fixed z-50 w-max body-2-regular ${border ? "font-tertiary" : ""}`}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          <div className={placement === "top" ? "pb-1" : "pt-2"}>
            <ul className="surface-primary shadow-lg rounded-lg border outline-quaternary">
              {filteredItems.map((item: string, index: number) => (
                <li
                  key={index}
                  className={`relative mx-1 my-0.5 px-3 py-0.5 rounded-lg hover:surface-secondary cursor-pointer flex items-center justify-between ${
                    item === "delete" ? "font-error" : ""
                  }`}
                  onMouseEnter={(e) => {
                    if (onHoverAction) onHoverAction(item);
                    if (nestedItems[item])
                      handleNestedMenuPosition(item, e.currentTarget);
                    else setActiveNestedMenu(null);
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!nestedItems[item]) {
                      handleSelect(item, changeDefault);
                      setIsDropdownOpen(false);
                    }
                  }}
                >
                  <div className="flex flex-row w-full justify-between items-center">
                    <div className="flex items-center">
                      {item === "archive" && (
                        <IconArchive size={16} className="mr-2" />
                      )}
                      {item === "unarchive" && (
                        <IconArchiveOff size={16} className="mr-2" />
                      )}
                      {item === "duplicate" && (
                        <IconCopy size={16} className="mr-2" />
                      )}
                      {item === "delete" && (
                        <IconTrash size={16} className="mr-2 font-error" />
                      )}
                      {item === "edit" && (
                        <IconEdit size={16} className="mr-2" />
                      )}
                      <span>{getLabel(item)}</span>
                    </div>
                    {nestedItems[item] && (
                      <IconChevronRight size={16} className="ml-2" />
                    )}
                  </div>

                  {/* Nested Menu */}
                  {activeNestedMenu?.item === item && nestedItems[item] && (
                    <div
                      className={`absolute surface-primary shadow-lg rounded-lg border min-w-30 outline-quaternary ${
                        activeNestedMenu.side === "right"
                          ? "left-full"
                          : "right-full"
                      } ${activeNestedMenu.vAlign === "top" ? "top-0" : "bottom-0"}`}
                    >
                      <ul>
                        {nestedItems[item].map(
                          (subItem: string, subIndex: number) => (
                            <li
                              key={`${item}-${subIndex}`}
                              className="mx-1 my-0.5 px-3 py-0.5 rounded-lg hover:surface-secondary cursor-pointer"
                              onMouseEnter={() =>
                                onHoverAction &&
                                onHoverAction(`${item}/${subItem}`)
                              }
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onNestedAction) {
                                  onNestedAction(item, subItem);
                                } else {
                                  handleSelect(
                                    `${item}/${subItem}`,
                                    changeDefault,
                                  );
                                }
                                setIsDropdownOpen(false);
                              }}
                            >
                              {getLabel(subItem)}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
